import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import isEqual from 'deep-equal';

import { queryParamToString } from 'shared/util/query';
import { setInitialBannerFilterValues } from 'shared/banners/bannerParams';
import { trackResultPage } from '@/lib/tracking/tracking';
import * as resultActions from '@/ducks/results/resultsActions';
import { TopBar } from './components/topBar/TopBar';
import { MobileTopBar } from './components/topBar/MobileTopBar';
import { ResultList } from './components/resultList/ResultList';
import { ResultFilter } from './components/resultFilter/ResultFilter';
import { Sorting } from './components/sorting/Sorting';
import { SmallFhhMap } from './components/smallFhhMap/SmallFhhMap';
import { FhhSearch } from './components/newSearch/FhhSearch';
import { SearchDetails } from './components/newSearch/SearchDetails';
import type { DeviceType, FhhState, Filters, ResultData } from '@/domain';

export interface ResultPageProps {
    deviceType: DeviceType;
    loginId: number;
}

const hasSearchParams = (filters: Filters) => {
    return Boolean(
        (filters.city && filters.city?.length > 0) ||
            (filters.country && filters.country?.length > 0) ||
            (filters.booking_from && filters.booking_from?.length > 0 && filters.booking_to && filters.booking_to?.length > 0) ||
            (filters.no_of_bedrooms_from && filters.no_of_bedrooms_from > 0) ||
            (filters.no_of_beds_from && filters.no_of_beds_from > 0),
    );
};

export const ResultPage = ({ deviceType, loginId }: ResultPageProps) => {
    const router = useRouter();
    const dispatch = useDispatch();

    const { data, filters, filterData, autocompleteData, showFiltersOverlay } = useSelector((state: FhhState) => state.results);

    const isMobile = deviceType.isProbablyMobile;

    const [openSearchForm, setOpenSearchForm] = useState(false);
    const showSearchForm = openSearchForm || !(isMobile && hasSearchParams(filters));

    useEffect(() => {
        if (!isFalsyOrEmpty(router?.query)) {
            dispatch(resultActions.updateFilters({ ...router.query, nrFUSAds: deviceType.isProbablyMobile ? 1 : 2 }));
        }
        populateResultsIfEmpty();

        setInitialBannerFilterValues({
            pageType: 'result',
            page: filters.page ?? undefined,
            destination: {
                city: autocompleteData?.trackingInfo?.city || filters.city || queryParamToString(router?.query?.city),
                country: autocompleteData?.trackingInfo?.country || filters.country || queryParamToString(router?.query?.country),
            },
        });
    }, []);

    const prevFilters = useRef<Filters>(filters);
    const prevData = useRef<ResultData>();
    useEffect(() => {
        const unchangedFilters = isEqual(filters, prevFilters.current);

        if (unchangedFilters && isEqual(data, prevData.current)) {
            return;
        }
        prevFilters.current = filters;
        prevData.current = data;
        // On a page load, the stats object is empty. This object is needed for tracking, so must wait until
        // next update when stats is set before tracking. After that, track on every filter change.
        if (data.stats?.numResults !== undefined) {
            trackResultPage('Grid', { data, filters });
        }

        if (unchangedFilters) {
            return;
        }
        router?.replace({ pathname: router.pathname, query: filters }, undefined, { shallow: true });
    }, [filters, data.stats]);

    const populateResultsIfEmpty = () => {
        const query = router?.query || {};

        if (data.resultItems === null) {
            dispatch(resultActions.populateResults(Object.assign({}, filters, query))); // this is the trigger for the default search
        }
    };

    const isFalsyOrEmpty = (value) =>
        !value || (Object.prototype.toString.call(value) === '[object Object]' && JSON.stringify(value) === '{}');

    const filterResults = (filter: Filters) => dispatch(resultActions.filterResults(filter));
    const updateAutocomplete = (selected, trackingInfo = null) => dispatch(resultActions.updateAutocomplete(selected, trackingInfo));

    return (
        <div data-testid="resultPage">
            <MobileTopBar
                stats={data.stats}
                autocompleteData={autocompleteData}
                filters={filters}
                hideFilters={() => dispatch(resultActions.hideFilters())}
                showFilters={() => dispatch(resultActions.showFilters())}
                showFiltersOverlay={Boolean(showFiltersOverlay)}
                isLoggedIn={loginId != null}
            />
            <TopBar stats={data.stats} onFilterChanged={filterResults} filters={filters} />
            <div className={!showFiltersOverlay ? undefined : 'hidden md:block'}>
                {showSearchForm ? (
                    <FhhSearch filters={filters} />
                ) : (
                    <SearchDetails filters={filters} openSearchForm={() => setOpenSearchForm(true)} />
                )}
            </div>
            <section className="grid md:grid-cols-3 gap-32">
                <div className={showFiltersOverlay ? undefined : 'hidden md:block'} data-testid="Sidebar-desktop">
                    <ResultFilter
                        autocompleteData={autocompleteData}
                        updateAutocomplete={updateAutocomplete}
                        filters={filters}
                        filterData={filterData}
                        onFilterChanged={filterResults}
                        isLoggedIn={loginId != null}
                        isMobile={isMobile}
                    />
                </div>
                <div className={`md:col-span-2${showFiltersOverlay ? ' hidden md:block' : ''}`}>
                    <div className="md:hidden">
                        <SmallFhhMap filters={filters} />
                        <Sorting filters={filters} onFilterChanged={filterResults} />
                    </div>
                    <ResultList
                        results={data.resultItems}
                        stats={data.stats}
                        filters={filters}
                        onFilterChanged={filterResults}
                        numberOfPreloadedImages={6}
                        isMobile={isMobile}
                        loginId={loginId}
                    />
                </div>
            </section>
        </div>
    );
};
